/**
 * Display rows for the thread: one bubble per send, with a key that survives the
 * optimistic copy being replaced by the canonical one.
 *
 * The canonical user message can arrive over the socket a moment before the HTTP
 * ack swaps out the optimistic copy, so for that window both sit in state. The
 * display drops the optimistic copy when the canonical one carrying its request id
 * comes after it, and both rows key by that request id, so React keeps the same
 * element and the entrance never replays. State is left untouched.
 */

import { clientRequestIdOf, pendingClientRequestIdOf } from '../uncertainDelivery'

import type { ChatRoomMessageDto } from '@digital-twins/shared-types'

export interface DisplayMessages {
  messages: Array<ChatRoomMessageDto>
  /** React key per shown message: its request id when it has one, else its id. */
  keys: Map<ChatRoomMessageDto, string>
}

export function toDisplayMessages(messages: Array<ChatRoomMessageDto>): DisplayMessages {
  const pendingIndexByRequestId = new Map<string, number>()
  messages.forEach((message, index) => {
    const requestId = pendingClientRequestIdOf(message)
    if (requestId) {
      pendingIndexByRequestId.set(requestId, index)
    }
  })

  // Only a canonical copy after the optimistic one replaces it: an earlier row
  // with a reused request id is a different message and keeps its own bubble.
  const replacedIndexes = new Set<number>()
  const carriedImages = new Map<number, ChatRoomMessageDto['images']>()
  messages.forEach((message, index) => {
    const requestId = clientRequestIdOf(message)
    const pendingIndex = requestId ? pendingIndexByRequestId.get(requestId) : undefined
    if (pendingIndex === undefined || pendingIndex > index) {
      return
    }
    replacedIndexes.add(pendingIndex)
    const pendingImages = messages[pendingIndex].images
    if (pendingImages && pendingImages.length > 0 && !(message.images && message.images.length > 0)) {
      carriedImages.set(index, pendingImages)
    }
  })

  const shown: Array<ChatRoomMessageDto> = []
  const keys = new Map<ChatRoomMessageDto, string>()
  const usedKeys = new Set<string>()
  messages.forEach((message, index) => {
    if (replacedIndexes.has(index)) {
      return
    }
    const images = carriedImages.get(index)
    const row = images ? { ...message, images } : message
    const preferred = clientRequestIdOf(message) ?? pendingClientRequestIdOf(message) ?? message.id
    const key = usedKeys.has(preferred) ? message.id : preferred
    usedKeys.add(key)
    keys.set(row, key)
    shown.push(row)
  })

  return { messages: shown, keys }
}
